/**
 * Everything the Flow screen does, without how it looks.
 *
 * A flow is drawn, not built: the curves (energy, BPM, danceability over time)
 * are the only thing a person places; the songs are the engine's answer,
 * re-curated whenever a curve, a filter or a rule changes.
 *
 * It plays through Nova's one player: Play flow puts the songs in the queue,
 * and the transport at the bottom plays them like any other. While it plays,
 * a change keeps the songs already played and the one playing, and replaces
 * the rest of the queue, so a flow can be shaped by ear. Endless and repeating
 * flows top the queue up before it runs out. The crossfade is the player's.
 *
 * Kept apart from the screen so any layout -- or a plugin -- can drive a flow.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useApp } from "../core/app";
import { setCloseGuard } from "../core/closeGuard";
import { bus } from "../core/bus";
import { player as mainPlayer, usePlayer, type QueueTrack } from "../core/player";
import { EMPTY_FILTERS, filterClauses, type Filters } from "../search/filters";
import { parse } from "../search/parse";
import { curateFlow, deleteFlow, listFlows, loadFlow, saveFlow, type FlowSummary } from "./flowApi";
import {
  asHistory,
  defaultSpec,
  DEFAULT_RULES,
  flowPlan,
  keptThrough,
  mergePinned,
  retime,
  METRIC_ORDER,
  METRICS,
  rescaleCurves,
  sectionTotal,
  type FlowMode,
  type FlowPick,
  type FlowRules,
  type FlowSpec,
  type Metric,
} from "./flowMath";

/** How far ahead an endless or repeating flow is curated, and when to top it up. */
const CHUNK = 1800;
/** Top up when this few songs are left after the one playing. */
const TOP_UP_SONGS = 2;

let editors = 0;

/** A chosen song, as the player queues it. */
export function pickToQueue(p: FlowPick): QueueTrack {
  return {
    id: p.trackId, title: p.title ?? "Untitled", artist: p.artist ?? "", album: null, bpm: p.bpm,
    musicalKey: p.camelot, lufs: null, cueIn: p.cueIn, cueOut: p.cueOut, duration: p.duration,
  };
}

export function useFlowEditor(offline: boolean) {
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [flowId, setFlowId] = useState<number | null>(null);
  const [name, setName] = useState("Untitled flow");
  const [spec, setSpec] = useState<FlowSpec>(defaultSpec);
  const [savedKey, setSavedKey] = useState<string>(() => JSON.stringify(defaultSpec()));
  const [active, setActive] = useState<Metric>("energy");
  const [input, setInput] = useState("");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [picks, setPicks] = useState<FlowPick[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pool, setPool] = useState<number | null>(null);
  const [curating, setCurating] = useState(false);
  const [notice, setNotice] = useState<{ text: string; bad?: boolean } | null>(null);
  /** Nothing is curated until the Flow screen has been opened once: no work for a flow nobody looked at. */
  const [wanted, setWanted] = useState(false);
  const want = useCallback(() => setWanted(true), []);
  const ps = usePlayer();
  const sourceKey = useRef(`flow-${++editors}`).current;
  /** This screen's flow is what the player is playing. */
  const live = ps.source?.key === sourceKey;

  const typedQuery = useMemo(() => parse(input), [input]);
  const query = useMemo(
    () => ({ clauses: [...typedQuery.clauses, ...filterClauses(filters)], free: typedQuery.free }),
    [typedQuery, filters]
  );
  /** What the engine is sent and what is saved: the page's filters ride along to be restored. */
  const crossfade = ps.crossfade;
  const engineSpec = useMemo<FlowSpec>(
    // The crossfade is the player's, so the timing the engine plans is the timing you hear.
    () => ({ ...spec, rules: { ...spec.rules, crossfade, stretch: false }, query, input, filters }),
    [spec, query, input, filters, crossfade]
  );
  const dirty = JSON.stringify({ name, spec, input, filters }) !== savedKey;

  const plan = useMemo(() => flowPlan(picks, crossfade), [picks, crossfade]);
  /** Which chosen song is playing: the queue holds the picks in order. */
  const onAir = live && ps.index >= 0 && ps.index < picks.length ? ps.index : -1;
  const liveRef = useRef({ live, onAir });
  liveRef.current = { live, onAir };
  const picksRef = useRef(picks);
  picksRef.current = picks;
  /** Songs taken out of this flow by hand: never chosen again this session. */
  const removed = useRef(new Set<string>());

  const refreshFlows = useCallback(async () => setFlows(await listFlows()), []);
  useEffect(() => void refreshFlows(), [refreshFlows]);
  useEffect(() => bus.on("flows.changed", () => void refreshFlows()), [refreshFlows]);

  // ---------------------------------------------------------------- curation

  /** Bumped by every edit, so an answer to an older edit (or a top-up) is dropped. */
  const seq = useRef(0);
  const topUp = useRef(false);
  const specKey = JSON.stringify(engineSpec);

  useEffect(() => {
    if (offline || !wanted) return;
    const mine = ++seq.current;
    topUp.current = false;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setCurating(true);
      // Playing: keep what has played and the song playing; re-choose after it.
      const now = liveRef.current;
      const { kept, from } = keptThrough(picksRef.current, now.live ? now.onAir : -1);
      const until = spec.mode === "set" ? spec.length : Math.max(spec.length, from + CHUNK);
      try {
        const res = await curateFlow({ spec: engineSpec, start: from, history: asHistory(kept.slice(-60)), until }, controller.signal);
        if (mine !== seq.current) return;
        if (res.error) setNotice({ text: res.error, bad: true });
        else {
          // Songs placed by hand keep their places; the rest is the new answer.
          const merged = mergePinned(picksRef.current.slice(kept.length), res.picks, removed.current);
          setPicks([...kept, ...merged]);
          if (now.live) mainPlayer.replaceUpcoming(merged.map(pickToQueue), sourceKey, merged.map((p) => !!p.pinned));
          setWarnings(res.warnings);
          setPool(res.pool);
        }
      } catch {
        // Aborted by a newer edit.
      } finally {
        if (mine === seq.current) setCurating(false);
      }
    }, 350);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [specKey, offline, wanted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Endless and repeating flows: curate the next half hour before the queue runs out.
  useEffect(() => {
    if (spec.mode === "set" || !live || onAir < 0 || topUp.current || curating || !picks.length) return;
    if (picks.length - 1 - onAir > TOP_UP_SONGS) return;
    const mine = seq.current;
    const last = picks[picks.length - 1];
    const from = last.start + last.air;
    topUp.current = true;
    void curateFlow({ spec: engineSpec, start: from, history: asHistory(picks.slice(-60)), until: from + CHUNK })
      .then((res) => {
        if (mine !== seq.current || res.error) return;
        const more = res.picks.filter((p) => !removed.current.has(p.trackId));
        setPicks((all) => [...all, ...more]);
        mainPlayer.extend(more.map(pickToQueue), sourceKey);
      })
      .finally(() => {
        topUp.current = false;
      });
  }, [live, onAir, picks, spec.mode, curating, engineSpec]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- playing

  /** Play the flow in the main player, from the start or from the song at second *t*. */
  const play = (t = 0) => {
    if (!picks.length) return;
    let index = picks.findIndex((p) => t >= p.start && t < p.start + p.air);
    if (index < 0) index = t <= 0 ? 0 : picks.length - 1;
    if (live) void mainPlayer.jump(index, { fade: crossfade > 0 });
    else mainPlayer.playTracks(picks.map(pickToQueue), index, { kind: "flow", key: sourceKey, label: name });
  };
  // ---------------------------------------------------------------- by hand

  /** While playing, only what is still to come can be moved or taken out. */
  const movable = (i: number) => !live || i > onAir;
  /** Drag a song to a new place: it is pinned there until a new answer. */
  const movePick = (from: number, to: number) => {
    if (!movable(from) || !movable(to) || from === to) return;
    const next = [...picks];
    const [p] = next.splice(from, 1);
    next.splice(to, 0, { ...p, pinned: true });
    setPicks(retime(next, picks[0]?.start ?? 0));
    if (live) mainPlayer.move(from, to);
  };
  const setPinned = (i: number, pinned: boolean) => {
    setPicks((all) => all.map((p, k) => (k === i ? { ...p, pinned } : p)));
    if (live) mainPlayer.setPinned(i, pinned);
  };
  const removePick = (i: number) => {
    if (!movable(i)) return;
    removed.current.add(picks[i].trackId);
    setPicks(retime(picks.filter((_, k) => k !== i), picks[0]?.start ?? 0));
    if (live) mainPlayer.removeAt(i);
  };
  const handPlaced = picks.filter((p) => p.pinned).length;
  /** A different answer to the same curves. Songs placed by hand go back to Nova's choice. */
  const newAnswer = () => {
    if (handPlaced && !window.confirm(`${handPlaced} song${handPlaced === 1 ? " was" : "s were"} placed by hand. A new answer lets Nova choose those places again. Continue?`)) return;
    removed.current.clear();
    setPicks((all) => all.map((p) => (p.pinned ? { ...p, pinned: false } : p)));
    setRule("variation", spec.rules.variation + 1);
  };

  /** Where the flow is now, in seconds of the flow (for the chart's playhead). */
  const now = onAir >= 0 ? picks[onAir].start + Math.max(0, ps.position - (picks[onAir].cueIn ?? 0)) : null;

  // ---------------------------------------------------------------- editing

  const setRule = <K extends keyof FlowRules>(key: K, value: FlowRules[K]) =>
    setSpec((s) => ({ ...s, rules: { ...s.rules, [key]: value } }));

  const toggleMetric = (m: Metric) =>
    setSpec((s) => {
      const on = s.metrics.includes(m);
      const metrics = on ? s.metrics.filter((x) => x !== m) : METRIC_ORDER.filter((x) => x === m || s.metrics.includes(x));
      if (!on) setActive(m);
      else if (active === m && metrics.length) setActive(metrics[0]);
      const curves = { ...s.curves };
      if (!on && !curves[m]?.length) {
        const mid = (METRICS[m].min + METRICS[m].max) / 2;
        curves[m] = [{ t: 0, v: mid }, { t: s.length, v: mid }];
      }
      return { ...s, metrics, curves };
    });

  const setLength = (minutes: number) =>
    setSpec((s) => {
      // With sections, time is the sections' own: the length cannot go under them, and
      // the curves stay where they are drawn rather than stretching.
      const filled = Math.ceil(sectionTotal(s.sections) / 60);
      const length = Math.min(240, Math.max(5, filled, Math.round(minutes))) * 60;
      return { ...s, length, curves: s.sections?.length ? s.curves : rescaleCurves(s.curves, s.length, length) };
    });

  // ---------------------------------------------------------------- sections
  /** Add a section after the last: 20 minutes, or what is left of the flow if less. */
  const addSection = () =>
    setSpec((s) => {
      const left = s.length - sectionTotal(s.sections);
      if (left < 60) return s;
      const sections = [...(s.sections ?? [])];
      sections.push({ name: `Section ${sections.length + 1}`, length: Math.min(1200, Math.floor(left / 60) * 60) });
      return { ...s, sections };
    });
  /** Rename a section, or change its length (minutes; never past the flow's end). */
  const setSection = (i: number, patch: { name?: string; minutes?: number }) =>
    setSpec((s) => {
      const sections = [...(s.sections ?? [])];
      const cur = sections[i];
      if (!cur) return s;
      let length = cur.length;
      if (patch.minutes !== undefined && Number.isFinite(patch.minutes)) {
        const others = sectionTotal(sections) - cur.length;
        length = Math.max(60, Math.min(s.length - others, Math.round(patch.minutes) * 60));
      }
      sections[i] = { name: patch.name ?? cur.name, length };
      return { ...s, sections };
    });
  const removeSection = (i: number) =>
    setSpec((s) => ({ ...s, sections: (s.sections ?? []).filter((_, k) => k !== i) }));

  const setMode = (mode: FlowMode) => setSpec((s) => ({ ...s, mode }));
  const setCurve = (m: Metric, points: FlowSpec["curves"][Metric]) => setSpec((s) => ({ ...s, curves: { ...s.curves, [m]: points } }));
  const resetCurves = () => setSpec((s) => ({ ...s, curves: defaultSpec().curves }));
  const setSeed = (trackId: number | null, label: string | null) => setSpec((s) => ({ ...s, seedTrackId: trackId, seedLabel: label }));

  // ---------------------------------------------------------------- saved flows

  const remember = (n: string, s: FlowSpec, i: string, f: Filters) => setSavedKey(JSON.stringify({ name: n, spec: s, input: i, filters: f }));

  const newFlow = (seed?: { id: number; label: string }) => {
    const fresh = { ...defaultSpec(), seedTrackId: seed?.id ?? null, seedLabel: seed?.label ?? null };
    setFlowId(null);
    setName("Untitled flow");
    setSpec(fresh);
    setInput("");
    setFilters(EMPTY_FILTERS);
    setActive("energy");
    remember("Untitled flow", fresh, "", EMPTY_FILTERS);
  };

  const openFlow = async (id: number) => {
    const res = await loadFlow(id).catch(() => null);
    if (!res?.flow) {
      setNotice({ text: res?.error ?? "Could not open that flow.", bad: true });
      return;
    }
    const saved = res.flow.spec;
    const next: FlowSpec = { ...defaultSpec(), ...saved, rules: { ...DEFAULT_RULES, ...saved.rules } };
    const nextInput = typeof saved.input === "string" ? saved.input : "";
    const nextFilters = { ...EMPTY_FILTERS, ...((saved.filters as Filters | undefined) ?? {}) };
    setFlowId(res.flow.id);
    setName(res.flow.name);
    setSpec(next);
    setInput(nextInput);
    setFilters(nextFilters);
    setActive(saved.metrics?.[0] ?? "energy");
    remember(res.flow.name, next, nextInput, nextFilters);
  };

  const save = async (asNew = false): Promise<boolean> => {
    const res = await saveFlow(name, engineSpec, asNew ? null : flowId).catch((err) => ({ error: String(err) }));
    if ("flow" in res && res.flow) {
      setFlowId(res.flow.id);
      setName(res.flow.name);
      remember(res.flow.name, spec, input, filters);
      setNotice({ text: `Saved “${res.flow.name}”.` });
      void refreshFlows();
      return true;
    }
    setNotice({ text: `Not saved: ${"error" in res ? res.error : "unknown error"}`, bad: true });
    return false;
  };

  const remove = async () => {
    if (flowId === null) return newFlow();
    if (!window.confirm(`Delete the flow “${name}”? Songs and playlists are not touched.`)) return;
    await deleteFlow(flowId).catch(() => null);
    await refreshFlows();
    newFlow();
  };

  /** Delete any saved flow from the list, open or not. */
  const removeSaved = async (id: number, title: string) => {
    if (!window.confirm(`Delete the flow “${title}”? Songs and playlists are not touched.`)) return false;
    await deleteFlow(id).catch(() => null);
    await refreshFlows();
    if (id === flowId) newFlow();
    return true;
  };

  const exportable = spec.mode === "set" ? picks : picks.filter((p) => p.start < Math.max(spec.length, plan.total));
  const averageMatch = useMemo(() => {
    const scored = picks.filter((p) => p.match !== null);
    return scored.length ? Math.round(scored.reduce((s, p) => s + (p.match ?? 0), 0) / scored.length) : null;
  }, [picks]);

  return {
    want, flows, flowId, name, setName, spec, setSpec, active, setActive, input, setInput, typedQuery, filters, setFilters,
    picks, warnings, pool, curating, notice, setNotice, plan, live, playing: live && ps.playing, play, now, onAir, exportable,
    movable, movePick, setPinned, removePick, handPlaced, newAnswer,
    averageMatch, dirty, engineSpec,
    setRule, toggleMetric, setLength, setMode, setCurve, resetCurves, setSeed, newFlow, openFlow, save, remove, removeSaved,
    addSection, setSection, removeSection,
  };
}

export type FlowEditor = ReturnType<typeof useFlowEditor>;

// ---------------------------------------------------------------- the one open flow

/**
 * The flow that is open stays open, whatever screen or layout is showing, until
 * Nova closes: `FlowSession` (mounted once, in App) holds the editor, so its
 * curation and its top-ups keep running while Flow is not on screen, and the
 * Flow screen (`useFlow`) shows the same flow when it comes back. Closing Nova
 * with unsaved changes asks first (core/closeGuard.ts).
 */
let session: FlowEditor | null = null;
const sessionListeners = new Set<() => void>();

export function FlowSession(): null {
  const app = useApp();
  const f = useFlowEditor(app.offline);
  // Set during render so a Flow screen rendered in the same pass sees it at once.
  session = f;
  useEffect(() => sessionListeners.forEach((fn) => fn()));
  const guard = f.dirty ? `The flow “${f.name || "Untitled flow"}” has changes that are not saved${f.live ? " (it is playing)" : ""}. They are lost if Nova closes.` : null;
  useEffect(() => setCloseGuard("flow", guard), [guard]);
  return null;
}

/** The open flow (see FlowSession). */
export function useFlow(): FlowEditor | null {
  return useSyncExternalStore(
    (fn) => {
      sessionListeners.add(fn);
      return () => sessionListeners.delete(fn);
    },
    () => session
  );
}

/**
 * Opened from elsewhere (a saved flow by id, or a new one seeded with a song):
 * applied once per request, so coming back to the Flow screen does not reopen the
 * saved flow over the changes made since.
 */
let appliedParams: string | null = null;
export function takeFlowParams(key: string): boolean {
  if (key === appliedParams) return false;
  appliedParams = key;
  return true;
}
